import { useMutation } from '@tanstack/react-query'
import { App } from 'antd'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  usersLoginAdminCreateMutation,
  type QrLoginWaitApprovedResponse
} from 'src/api/main'
import { LOGIN_IN_PROGRESS_KEY, LOGOUT_ENFORCED_KEY } from 'src/constants'
import { useSessionContext } from 'src/hooks'
import { paths } from 'src/router'
import {
  getApiErrorMessages,
  setLocalStorageToken,
  UNEXPECTED_API_ERROR
} from 'src/utils'

import { QrSignInPanel } from './QrSignInPanel'

type Props = {
  variant?: 'full' | 'compact'
  /** Heading level for the panel title. Use 2 when it sits beside the form's
      h1 (right pane); 1 when the QR is the page's primary content (QR-only). */
  headingLevel?: 1 | 2
}

/**
 * QR sign-in for the login surface: owns the post-approval success path
 * (mirrors Login's `handleQrAuthenticated` — set tokens, load the session,
 * land home; intentionally no passkey prompt). Rendered by `LayoutAuth` in the
 * right panel (desktop) or below the card (mobile) when QR login is enabled.
 */
export const LoginQrPanel = ({ variant = 'full', headingLevel = 1 }: Props) => {
  const { message } = App.useApp()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { updateUserData } = useSessionContext()

  const { mutateAsync: fetchUserAfterLogin } = useMutation({
    ...usersLoginAdminCreateMutation(),
    onSuccess: (response) => {
      sessionStorage.removeItem(LOGIN_IN_PROGRESS_KEY)
      updateUserData(response)
      message.success(t('login_success'))
      navigate(paths.home)
    },
    onError: (e) => {
      sessionStorage.removeItem(LOGIN_IN_PROGRESS_KEY)
      // No inline surface here (this panel only renders the QR component), so
      // the rare post-approval user-fetch failure surfaces as a transient toast.
      message.error(getApiErrorMessages(e)[0] ?? UNEXPECTED_API_ERROR)
    }
  })

  const handleAuthenticated = useCallback(
    async (tokens: QrLoginWaitApprovedResponse) => {
      sessionStorage.removeItem(LOGOUT_ENFORCED_KEY)
      sessionStorage.setItem(LOGIN_IN_PROGRESS_KEY, '1')
      setLocalStorageToken(tokens)
      await fetchUserAfterLogin({})
    },
    [fetchUserAfterLogin]
  )

  return (
    <QrSignInPanel
      variant={variant}
      headingLevel={headingLevel}
      onAuthenticated={handleAuthenticated}
    />
  )
}
